import type { Fighter, Lunge, Scene } from './types';
import { ENTER_DUR, KNIFE_DEATH_FRAC, STANCE_STEP, nearestEnemyId } from './battleConfig';
import { aimTargetOf, type BattleEngineOpts, type PlaybackState } from './playbackState';
import { playSfx } from '../game/sfx';

// footstep cadence: a fighter still more than STEP_EPS px from its resting spot is "walking"
// (match-start entrance or a stance shift), so it drops a footstep every STEP_GAP seconds.
const STEP_EPS = 3;
const STEP_GAP = 0.36; // "medlenno" — slow, deliberate stride
const STEP_VOL = 0.22;  // many walk in at once, so keep each step soft

/** A knife charge: dash toward the target's LIVE position (it may be moving), a fraction of the
 *  way and back along a sine arc, striking at the apex. Killed mid-charge still lands the strike,
 *  then drops the corpse back on the knifer's own side. */
const advanceLunge = (sc: Scene, f: Fighter, lg: Lunge, n: number): void => {
  const tgt = sc.byId[lg.targetId];
  const destX = lg.homeX + ((tgt ? tgt.x : lg.homeX) - lg.homeX) * lg.frac;
  const destY = lg.homeY + ((tgt ? tgt.y : lg.homeY) - lg.homeY) * lg.frac;
  if (f.dead) {
    if (!lg.struck) { lg.struck = true; lg.strike(); }
    f.x = lg.homeX + (destX - lg.homeX) * (KNIFE_DEATH_FRAC / lg.frac) - f.dir * 12;
    f.y = lg.homeY + (destY - lg.homeY) * (KNIFE_DEATH_FRAC / lg.frac) + 28;
    f.lunge = undefined;
    return;
  }
  const p = (n - lg.t0) / lg.dur;
  if (p >= 1) { f.x = lg.homeX; f.y = lg.homeY; f.lunge = undefined; return; }
  const k = Math.sin(Math.PI * Math.max(0, p));
  f.x = lg.homeX + (destX - lg.homeX) * k;
  f.y = lg.homeY + (destY - lg.homeY) * k;
  if (!lg.struck && p >= 0.5) { lg.struck = true; lg.strike(); }
};

/** Smoothly rotate a fighter's aim toward `aimAt` (shortest way around). */
const turnToward = (f: Fighter, aimAt: number): void => {
  let d = aimAt - f.aim;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  f.aim += d * 0.3;
};

/** Per-frame fighter motion: recoil decays; an active knife charge dashes out and back; otherwise
 *  ease toward the current stance's resting spot and turn the barrel onto the engaged enemy (or
 *  hold straight when the "target" is a friendly you're smoking). */
export const advanceFighters = (
  sc: Scene, opts: BattleEngineOpts, n: number, replaying: boolean, state: PlaybackState,
): void => {
  const youId = opts.getYouId();
  for (const f of sc.fighters) {
    f.recoil = Math.max(0, f.recoil - 0.12);
    if (f.lunge) { advanceLunge(sc, f, f.lunge, n); continue; }
    if (f.dead) continue;
    f.targetX = f.spawnX + f.dir * (STANCE_STEP[f.combatStance] ?? 0);
    // match-start entrance: walk in from the side edge over ENTER_DUR (smoothstep), so
    // |targetX − x| stays large → the walk bob / run cycle plays the whole way in.
    const enterEnd = state.enterAt + ENTER_DUR;
    if (state.enterAt && n < enterEnd && f.enterFromX != null) {
      const p = Math.max(0, Math.min(1, (n - state.enterAt) / ENTER_DUR));
      const e = p * p * (3 - 2 * p);
      f.x = f.enterFromX + (f.targetX - f.enterFromX) * e;
    } else {
      f.x += (f.targetX - f.x) * 0.1;
    }
    // footsteps: ring while the fighter is still travelling to its spot (entrance or a stance
    // shift). Stagger the first step by field position so a whole squad walking in doesn't thud
    // in unison; clear the clock on stop so the next walk starts on a fresh step.
    const entering = !!state.enterAt && n < enterEnd && f.enterFromX != null;
    if (entering || Math.abs(f.targetX - f.x) > STEP_EPS) {
      if (f.stepAt == null) f.stepAt = n + ((((f.spawnX % 100) + 100) % 100) / 100) * STEP_GAP;
      else if (n >= f.stepAt) { playSfx('footstep', STEP_VOL); f.stepAt = n + STEP_GAP; }
    } else {
      f.stepAt = undefined;
    }
    const chosen = aimTargetOf(f.id, youId, replaying, state, opts);
    const friendly = !!chosen && sc.byId[chosen]?.team === f.team;
    // The barrel ALWAYS tracks a LIVE enemy, in priority order:
    //   1) the target it is CURRENTLY firing on (engagedId, advanced as targets die) — so it
    //      never lags on the round's first (now-dead) target while shooting a new one,
    //   2) else its round pick, while that's still alive,
    //   3) else the nearest living enemy.
    // A friendly "target" (smoking a teammate) isn't shot at — hold straight ahead.
    let aimId: string | null = null;
    if (f.engagedId && !sc.byId[f.engagedId]?.dead) aimId = f.engagedId;
    else if (chosen && !friendly && !sc.byId[chosen]?.dead) aimId = chosen;
    else if (!friendly) aimId = nearestEnemyId(sc, f);
    const tg = aimId ? sc.byId[aimId] : null;
    const aimAt = tg && !tg.dead
      ? Math.atan2(tg.y - f.y, tg.x - f.x)
      : (friendly ? (f.dir > 0 ? 0 : Math.PI) : null); // friendly target → hold straight toward the enemy side
    if (aimAt != null) turnToward(f, aimAt);
  }
};
